import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/hooks/use-toast';

export interface FavoriteRow {
  id: string;
  variant_id: string;
  created_at: string;
}

/** Brugerens gemte biler. Kræver login — RLS binder rækkerne til auth.uid(). */
export function useFavorites() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['favorites', userId],
    enabled: !!userId,
    staleTime: 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_favorites')
        .select('id, variant_id, created_at')
        .order('created_at', { ascending: false });
      if (error) throw new Error(error.message);
      return (data || []) as FavoriteRow[];
    },
  });

  const ids = new Set((query.data || []).map((f) => f.variant_id));

  const toggle = useMutation({
    mutationFn: async (variantId: string) => {
      if (!userId) throw new Error('login');
      if (ids.has(variantId)) {
        const { error } = await supabase
          .from('user_favorites')
          .delete()
          .eq('user_id', userId)
          .eq('variant_id', variantId);
        if (error) throw new Error(error.message);
        return 'removed' as const;
      }
      const { error } = await supabase
        .from('user_favorites')
        .insert({ user_id: userId, variant_id: variantId });
      if (error) throw new Error(error.message);
      return 'added' as const;
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['favorites', userId] });
      toast({
        title: result === 'added' ? 'Gemt som favorit' : 'Fjernet fra favoritter',
      });
    },
    onError: (err: Error) => {
      if (err.message === 'login') {
        toast({ title: 'Log ind for at gemme favoritter', variant: 'destructive' });
        return;
      }
      toast({ title: 'Kunne ikke gemme favoritten', description: err.message, variant: 'destructive' });
    },
  });

  return {
    favorites: query.data || [],
    favoriteIds: ids,
    isLoading: query.isLoading,
    isLoggedIn: !!userId,
    toggleFavorite: (variantId: string) => toggle.mutate(variantId),
    isToggling: toggle.isPending,
  };
}
